// Persistent meta store — crystals, cosmetic inventory, lifetime
// stats. Everything lives in a single localStorage key (`smp.meta.v2`)
// as a JSON blob. Synchronous API, no backend.
//
// Awards flow: matches end → main.ts calls recordMatch(result) →
// wallet computes + applies rewards → returns { crystalsEarned,
// levelBonus, killBonus } so the caller can show a popup.
//
// 2026-04-28 — collapsed the dual currency (crystals + specialPoints)
// into a single 💎 crystals balance. v1 storage is migrated by reset
// (per playtest decision: clean slate, no conversion math). The old
// shop's WIN_SPECIAL/TIE_SPECIAL bonuses fold into win/tie crystal
// kickers so winners still feel a separate punch.

import type { CosmeticSlot } from './cosmetics';
import { getCosmetic } from './cosmetics';

const STORAGE_KEY = 'smp.meta.v2';
const LEGACY_KEYS = ['smp.meta.v1'];

export type EquippedSlots = {
  hat: string | null;
  skin: string | null;
  mimic: string | null;
  pet: string | null;
  trail: string | null;
};

export type MetaState = {
  version: 2;
  crystals: number;
  bestLevel: number;
  matchesPlayed: number;
  matchesWon: number;
  matchesTied: number;
  totalKills: number;
  crystalsEarned: number;
  ownedCosmetics: string[];
  pullsTotal: number;
  equipped: EquippedSlots;
};

export type MatchResult = {
  won: boolean;
  tie: boolean;
  level: number;
  kills: number;
};

export type MatchRewards = {
  crystalsEarned: number;
  levelBonus: number;
  killBonus: number;
};

const REWARDS = {
  PARTICIPATION_CRYSTALS: 50,
  WIN_CRYSTALS: 200,   // was 150 + a separate WIN_SPECIAL=10; rolled together.
  TIE_CRYSTALS: 100,   // was 75 + TIE_SPECIAL=5; rolled together.
  PER_KILL_CRYSTALS: 10,
  PER_LEVEL_CRYSTALS: 15,
} as const;

function freshEquipped(): EquippedSlots {
  return { hat: null, skin: null, mimic: null, pet: null, trail: null };
}

function freshState(): MetaState {
  return {
    version: 2,
    crystals: 0,
    bestLevel: 1,
    matchesPlayed: 0,
    matchesWon: 0,
    matchesTied: 0,
    totalKills: 0,
    crystalsEarned: 0,
    ownedCosmetics: [],
    pullsTotal: 0,
    equipped: freshEquipped(),
  };
}

let cache: MetaState | null = null;
const listeners = new Set<() => void>();

function load(): MetaState {
  if (cache) return cache;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<MetaState> & { version?: number };
      if (parsed.version === 2) {
        const merged: MetaState = { ...freshState(), ...parsed } as MetaState;
        merged.equipped = { ...freshEquipped(), ...(parsed.equipped ?? {}) };
        cache = merged;
        return cache;
      }
    }
    // v1 → v2 migration: hard reset per the 2026-04-28 unification
    // decision. We drop the old key so a future legacy load doesn't
    // come back and confuse the cache.
    for (const legacy of LEGACY_KEYS) {
      try { localStorage.removeItem(legacy); } catch { /* ignore */ }
    }
  } catch { /* ignore — fall back to fresh */ }
  cache = freshState();
  return cache;
}

function save(): void {
  if (!cache) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch { /* quota or disabled — tolerate */ }
  for (const l of listeners) l();
}

export function onChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function getState(): Readonly<MetaState> {
  return load();
}

export function getCrystals(): number {
  return load().crystals;
}

export function getWinRate(): number {
  const s = load();
  return s.matchesPlayed > 0 ? s.matchesWon / s.matchesPlayed : 0;
}

export function ownsItem(id: string): boolean {
  return load().ownedCosmetics.includes(id);
}

export function addCrystals(n: number): void {
  const s = load();
  s.crystals = Math.max(0, s.crystals + n);
  if (n > 0) s.crystalsEarned += n;
  save();
}

export function spendCrystals(n: number): boolean {
  const s = load();
  if (s.crystals < n) return false;
  s.crystals -= n;
  save();
  return true;
}

export function grantItem(id: string): void {
  const s = load();
  if (!s.ownedCosmetics.includes(id)) {
    s.ownedCosmetics.push(id);
    save();
  }
}

export function getEquipped(slot: CosmeticSlot): string | null {
  return load().equipped[slot];
}

export function getAllEquipped(): Readonly<EquippedSlots> {
  return load().equipped;
}

export function equip(id: string): boolean {
  const def = getCosmetic(id);
  if (!def) return false;
  const s = load();
  if (!s.ownedCosmetics.includes(id)) return false;
  s.equipped[def.slot] = id;
  save();
  return true;
}

export function unequip(slot: CosmeticSlot): void {
  const s = load();
  s.equipped[slot] = null;
  save();
}

export function isEquipped(id: string): boolean {
  const def = getCosmetic(id);
  if (!def) return false;
  return load().equipped[def.slot] === id;
}

export function registerPull(): void {
  const s = load();
  s.pullsTotal += 1;
  save();
}

export function recordMatch(result: MatchResult): MatchRewards {
  const s = load();
  s.matchesPlayed += 1;
  if (result.won) s.matchesWon += 1;
  else if (result.tie) s.matchesTied += 1;
  if (result.level > s.bestLevel) s.bestLevel = result.level;
  s.totalKills += result.kills;

  const killBonus = REWARDS.PER_KILL_CRYSTALS * result.kills;
  const levelBonus = REWARDS.PER_LEVEL_CRYSTALS * result.level;
  let crystalsEarned = REWARDS.PARTICIPATION_CRYSTALS + killBonus + levelBonus;
  if (result.won) {
    crystalsEarned += REWARDS.WIN_CRYSTALS;
  } else if (result.tie) {
    crystalsEarned += REWARDS.TIE_CRYSTALS;
  }
  s.crystals += crystalsEarned;
  s.crystalsEarned += crystalsEarned;
  save();
  return { crystalsEarned, levelBonus, killBonus };
}

// Debug / test helper — fully wipes the meta state. Not bound to UI.
export function resetAll(): void {
  cache = freshState();
  save();
}
